import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { SidebarSectionType } from 'data/types';
import { Fragment, useMemo } from 'react';
import {
  getAriaLabel,
  getCircleClasses,
  getLineColorClass,
  shouldStackLabel
} from 'utils/ui';
import Tooltip from './tooltip';

type ProgressBarStep = {
  id: string;
  label: string;
  title: string;
  requirementsMet: boolean;
  disabled?: boolean;
  sectionIds: string[];
};

function groupSections(sections: SidebarSectionType[]): ProgressBarStep[] {
  const steps: ProgressBarStep[] = [];

  for (const section of sections) {
    const group = section.progressBarGroup;
    const prev = steps.at(-1);

    if (group && prev?.id === `group:${group}`) {
      prev.sectionIds.push(section.id);
      prev.requirementsMet =
        prev.requirementsMet && section.requirementsMet === true;
      if (section.disabled) prev.disabled = true;
    } else {
      steps.push({
        id: group ? `group:${group}` : section.id,
        label: section.progressBarLabel ?? section.title,
        title: section.title,
        requirementsMet: section.requirementsMet === true,
        disabled: section.disabled,
        sectionIds: [section.id]
      });
    }
  }

  return steps;
}

type StepProgressBarProps = {
  readonly sections: SidebarSectionType[];
  readonly currentSectionId: string;
  readonly onSectionClick: (sectionId: string) => void;
};

export default function StepProgressBar({
  sections,
  currentSectionId,
  onSectionClick
}: StepProgressBarProps) {
  const initialSections = sections.filter(
    (s) => s.requirementsMet !== undefined
  );

  const displaySteps = useMemo(
    () => groupSections(initialSections),
    [initialSections]
  );

  return (
    <div className='flex w-full items-center px-5 pb-9 pt-5'>
      {displaySteps.map((step, index) => {
        const previousSteps = displaySteps.slice(0, index);
        const allPreviousComplete = previousSteps.every(
          (s) => s.requirementsMet
        );
        const isEnabled = allPreviousComplete && !step.disabled;
        const isCurrent = step.sectionIds.includes(currentSectionId);
        const isComplete = step.requirementsMet && allPreviousComplete;
        const clickTargetId = step.sectionIds[0];

        return (
          <Fragment key={step.id}>
            {index > 0 && (
              <div
                className={clsx(
                  'h-1 flex-1 transition-all duration-200',
                  getLineColorClass(allPreviousComplete)
                )}
              />
            )}
            <button
              type='button'
              disabled={!isEnabled}
              onClick={() => isEnabled && onSectionClick(clickTargetId)}
              className={clsx(
                'relative flex items-center justify-center',
                isEnabled ? 'cursor-pointer' : 'cursor-not-allowed'
              )}
              aria-label={getAriaLabel(step.title, isComplete, isCurrent)}
            >
              <div className='relative z-10'>
                <div
                  className={clsx(
                    'flex h-6 w-6 items-center justify-center rounded-full border-[3px] transition-all duration-200',
                    getCircleClasses(isComplete, isCurrent, isEnabled)
                  )}
                >
                  {isComplete && !isCurrent && (
                    <CheckIcon className='h-4 w-4 text-white' />
                  )}
                  {isCurrent && (
                    <span
                      aria-hidden='true'
                      className='h-2.5 w-2.5 rounded-full bg-green-600'
                    />
                  )}
                </div>
                <div className='absolute inset-0 z-20'>
                  <Tooltip
                    text={`${step.title} (${
                      step.requirementsMet
                        ? 'Requirements Met'
                        : 'Requirements Not Met'
                    })`}
                  />
                </div>
              </div>
              <span
                className={clsx(
                  'absolute left-1/2 top-full w-max -translate-x-1/2 pt-1 text-center text-xs leading-tight',
                  isCurrent ? 'font-semibold text-gray-900' : 'text-gray-500'
                )}
              >
                {shouldStackLabel(step.label)
                  ? step.label.split(' ').map((word) => (
                      <span key={word} className='block'>
                        {word}
                      </span>
                    ))
                  : step.label}
              </span>
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}
